import {
  createTreatyClient,
  firstTimeLoginClient,
  projectNameHeaders,
  type ProjectName,
} from "@neontree/bible-api-client";
import { createAuthClient } from "better-auth/react";
import {
  magicLinkClient,
  oneTimeTokenClient,
} from "better-auth/client/plugins";
import {
  AuthSession,
  BaseUserCustomProperties,
  UpdatedUser,
  User,
  UserProfileUpdate,
} from "./types";
import { hashWithoutToken } from "./redactToken";
import { resolveUpdatedUser, resolveUser } from "./resolvers";

const webappURL = import.meta.env.VITE_WEB_APP_URL;
const serverURL = import.meta.env.VITE_CORE_API_URL;

const BEARER_TOKEN_KEY = "bearer_token";

/**
 * A core API call that answered with an error. Carries the HTTP status so
 * callers can tell "too many attempts" (429) or "not allowed" apart from a
 * plain failure; `status` is undefined when the request never got a
 * response (offline).
 */
export class CoreAPIError extends Error {
  readonly status: number | undefined;
  /** The server's error code (e.g. `INVALID_TOKEN`), when it sent one. */
  readonly code: string | undefined;

  constructor(message: string, status?: number, code?: string) {
    super(message);
    this.name = "CoreAPIError";
    this.status = status;
    this.code = code;
  }
}

/** The error half of an auth `$fetch` reply. `code` is in the body but not in the client's types. */
type AuthFetchError = { status?: number; message?: string; code?: string };

const AUTH_CALL_TIMEOUT_MS = 10_000;

/**
 * Runs an auth call with a signal that aborts it after 10 s. Our own abort,
 * not better-fetch's `timeout`: that timer stops at the headers, so a stalled
 * body would hang the caller.
 */
async function withTimeout<T>(
  call: (signal: AbortSignal) => Promise<T>,
): Promise<T> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), AUTH_CALL_TIMEOUT_MS);
  try {
    return await call(controller.signal);
  } finally {
    clearTimeout(timer);
  }
}

/**
 * A live-chat ticket, or why there is none. `no-session` (401): re-check the
 * login before retrying, since the server also says 401 when it can't read
 * the session. `failed` is anything else (offline, 5xx): retry later.
 */
export type ChatTicketResult =
  | { ok: true; ticket: string }
  | { ok: false; reason: "no-session" | "failed" };

/**
 * Per-app settings the shared auth layer cannot know on its own. Structurally
 * satisfied by a `BrandConfig`, so apps can pass their brand object directly.
 */
export interface CoreAPIConfig {
  /** Sent as the `x-nt-project` header on every core API request. */
  projectName: ProjectName;
}

let config: CoreAPIConfig | null = null;

/**
 * Register the app's identity with the core API client. Call once at module
 * scope in the app's router (before `createBrowserRouter`) — the clients are
 * built lazily on first use, so anything that runs during render or in an
 * effect is guaranteed to see the configuration.
 */
export function configureCoreAPI(input: CoreAPIConfig): void {
  config = { projectName: input.projectName };
}

function requireConfig(): CoreAPIConfig {
  if (!config) {
    throw new Error(
      "configureCoreAPI() must be called before using coreapi — do it at module scope in the app router",
    );
  }
  return config;
}

function getStoredToken(): string | undefined {
  return localStorage.getItem(BEARER_TOKEN_KEY) ?? undefined;
}

/**
 * `Authorization` header for the current session, or `{}` when logged out.
 * Read on every call (not once), so tokens stored after a client is created
 * are picked up. Use it as the `headers` option of any API client that
 * authenticates through the core session — e.g. the apps' platform API
 * clients, whose server forwards the header to the core API.
 */
export function getAuthorizationHeaders(): Record<string, string> {
  const token = getStoredToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/** Forget the bearer token: the next session check is unauthenticated. */
export function clearStoredToken(): void {
  localStorage.removeItem(BEARER_TOKEN_KEY);
}

/**
 * The magic-link verify redirect hands the session token over in the URL
 * fragment (`#token=…`, set by the core API's redirect plugin — headers
 * don't survive a 302). Store it and scrub it from the address bar in one
 * step, *before* anything else can read the URL: Sentry's pageload
 * transaction and replay record `location.href`, and a fragment is also
 * kept in history. The checkout's one-time `#code=` is taken the same way,
 * and swapped later by `settleHandoffCode`. Idempotent; returns whether a
 * token or code was found. Call it first thing in the app entry, ahead of
 * `Sentry.init`.
 */
export function consumeAuthTokenFromURL(): boolean {
  if (typeof window === "undefined") return false;
  const hash = window.location.hash;
  if (!hash) return false;

  const params = new URLSearchParams(hash.slice(1));
  const token = params.get("token");
  const code = params.get("code");
  if (!token && !code) return false;

  if (token) localStorage.setItem(BEARER_TOKEN_KEY, token);
  // The checkout's one-time code. Held in memory: the swap is async and
  // must finish before the session check (AuthStoreProvider).
  if (code) takenHandoffCode = code;
  window.history.replaceState(
    window.history.state,
    "",
    window.location.pathname + window.location.search + hashWithoutToken(hash),
  );
  return true;
}

/** Where the emailed reset link lands in the app. */
export const RESET_PASSWORD_ROUTE = "/reset-password";

/** Where `RouteGuard` parks a logged-in user who has no password yet. */
export const SET_PASSWORD_ROUTE = "/set-password";

const RESET_TOKEN_KEY = "reset_password_token";
const RESET_UNCLEAR_KEY = "reset_password_unclear";
// For browsers where sessionStorage throws (blocked storage).
let resetTokenInMemory: string | null = null;
let resetSaveUnclearInMemory = false;

/**
 * The reset email's link lands on `/reset-password?token=…`. That token logs
 * its holder in for an hour, so take it out of the address bar before anything
 * records the URL. It is kept in sessionStorage so a reload, or a tab the phone
 * dropped while the user was in their password manager, doesn't lose it.
 * Idempotent. Call it first thing in the app entry.
 */
export function takeResetTokenFromURL(): void {
  if (typeof window === "undefined") return;
  if (window.location.pathname.replace(/\/+$/, "") !== RESET_PASSWORD_ROUTE) {
    return;
  }

  const params = new URLSearchParams(window.location.search);
  const token = params.get("token");
  if (!token) return;

  resetTokenInMemory = token;
  // A fresh link: what happened to an older one no longer matters.
  clearResetSaveUnclear();
  try {
    sessionStorage.setItem(RESET_TOKEN_KEY, token);
  } catch {
    // The in-memory copy covers this page view.
  }

  params.delete("token");
  const rest = params.toString();
  window.history.replaceState(
    window.history.state,
    "",
    window.location.pathname + (rest ? "?" + rest : "") + window.location.hash,
  );
}

/** The reset token taken from the URL, if this tab still holds one. */
export function heldResetToken(): string | null {
  if (resetTokenInMemory) return resetTokenInMemory;
  try {
    return sessionStorage.getItem(RESET_TOKEN_KEY);
  } catch {
    return null;
  }
}

/** Drop the reset token: it was used, or the server said it is dead. */
export function forgetResetToken(): void {
  resetTokenInMemory = null;
  try {
    sessionStorage.removeItem(RESET_TOKEN_KEY);
  } catch {
    // Nothing was stored.
  }
}

/**
 * A save got no clear answer (lost reply, server error). It may still have
 * worked, and that uses the link up: remember it, so a later "dead link"
 * answer isn't shown as an expired link. Kept across a reload, and after the
 * token is dropped, until a save works or a fresh link arrives.
 */
export function markResetSaveUnclear(): void {
  resetSaveUnclearInMemory = true;
  try {
    sessionStorage.setItem(RESET_UNCLEAR_KEY, "1");
  } catch {
    // The in-memory copy covers this page view.
  }
}

export function wasResetSaveUnclear(): boolean {
  if (resetSaveUnclearInMemory) return true;
  try {
    return sessionStorage.getItem(RESET_UNCLEAR_KEY) === "1";
  } catch {
    return false;
  }
}

function clearResetSaveUnclear(): void {
  resetSaveUnclearInMemory = false;
  try {
    sessionStorage.removeItem(RESET_UNCLEAR_KEY);
  } catch {
    // Nothing was stored.
  }
}

let takenHandoffCode: string | null = null;
let handoffExchange: Promise<void> | null = null;

/**
 * Swap the `#code=` taken from the URL, if any. Every caller waits on the
 * same swap: StrictMode runs the mount effect twice, and the second run
 * must not check the session before the first swap lands.
 */
export function settleHandoffCode(): Promise<void> {
  if (takenHandoffCode) {
    const code = takenHandoffCode;
    takenHandoffCode = null;
    handoffExchange = coreapi.exchangeHandoffCode(code).then(() => undefined);
  }
  return handoffExchange ?? Promise.resolve();
}

/**
 * A session-check error that means "logged out", so the caller may drop the
 * stored token. Only 401: the core API never answers the check with 403, so
 * a 403 comes from something in front of it (a proxy, a firewall) and says
 * nothing about the login.
 */
function meansNoSession(status: number | undefined): boolean {
  return status === 401;
}

/**
 * Session transport is bearer-only: the JWT the server hands out on sign-in
 * (`set-auth-token` header) lives in localStorage and is sent as an
 * `Authorization` header on every request. The apps run on origins other
 * than the API's, where third-party cookies are unreliable, so the login
 * client sends none (`credentials: "omit"`; better-auth's own default is
 * `"include"`). Lovelica's live chat logs in with a one-time ticket instead
 * (`getChatTicket`).
 */
function createClients(cfg: CoreAPIConfig) {
  const authClient = createAuthClient({
    baseURL: serverURL,
    basePath: "/auth",
    plugins: [magicLinkClient(), firstTimeLoginClient(), oneTimeTokenClient()],
    fetchOptions: {
      headers: projectNameHeaders(cfg.projectName),
      // Carry the token on auth calls too, so getSession resolves the bearer
      // session and signOut revokes it server-side.
      auth: { type: "Bearer", token: getStoredToken },
      credentials: "omit",
      onSuccess: (ctx) => {
        const token = ctx.response.headers.get("set-auth-token");
        if (token) {
          localStorage.setItem(BEARER_TOKEN_KEY, token);
        }
      },
    },
  });

  const coreapiClient = createTreatyClient(serverURL, {
    projectName: cfg.projectName,
    headers: getAuthorizationHeaders,
    // Eden's default reviver turns *any* date-looking string into a Date
    // (a user's name, a custom property). Server dates arrive as ISO
    // strings and nothing here calls Date methods on them, so leave them.
    parseDate: false,
  });

  return { authClient, coreapiClient };
}

type Clients = ReturnType<typeof createClients>;

/**
 * Extra detail on an audit event. Small and flat — the server refuses anything
 * else: at most 10 keys, string values up to 200 characters. Ids, outcomes and
 * names only. **Never content**: no message text, nothing the user typed, no
 * profile values.
 */
export type AuditEventMeta = Record<string, string | number | boolean | null>;

export interface AuditEventOptions {
  meta?: AuditEventMeta;
  /** For a tally ("120 seconds listened") instead of one event per unit. */
  count?: number;
}

export class CoreAPI {
  private clients: Clients | null = null;

  constructor() {}

  private get authClient(): Clients["authClient"] {
    return this.getClients().authClient;
  }

  private get client(): Clients["coreapiClient"] {
    return this.getClients().coreapiClient;
  }

  private getClients(): Clients {
    if (!this.clients) {
      this.clients = createClients(requireConfig());
    }
    return this.clients;
  }

  sendMagicLink = async (input: {
    email: string;
    callbackURL?: string;
  }): Promise<void> => {
    const { error } = await this.authClient.signIn.magicLink({
      email: input.email,
      callbackURL: input.callbackURL ?? webappURL,
      newUserCallbackURL: webappURL, //FIXME: add proper link
      // Failed verifies land straight on the login page, which reads the
      // `?error=<code>` param better-auth appends and shows a "link is old,
      // request a fresh one" banner.
      errorCallbackURL: webappURL + "/login",
    });

    if (error) {
      throw new CoreAPIError(
        error.message ?? "Failed to send the login link",
        error.status,
      );
    }
  };

  loginWithPassword = async (input: {
    email: string;
    password: string;
  }): Promise<void> => {
    const { error } = await this.authClient.signIn.email({
      email: input.email,
      password: input.password,
    });

    if (error) {
      throw new Error(error.message);
    }
  };

  /**
   * Ask for a "Reset your password" email. The server answers the same
   * whether or not the email has an account, so success means "asked", not
   * "sent".
   */
  requestPasswordReset = async (input: { email: string }): Promise<void> => {
    const { error } = await this.authClient.requestPasswordReset({
      email: input.email,
      redirectTo: webappURL + RESET_PASSWORD_ROUTE,
    });

    if (error) {
      throw new CoreAPIError(
        error.message ?? "Failed to send the reset link",
        error.status,
        error.code,
      );
    }
  };

  /** Save a new password with the emailed token, and log in with it. */
  resetPasswordAndSignIn = async (input: {
    token: string;
    newPassword: string;
  }): Promise<void> => {
    const { data, error } = await this.authClient.$fetch<{ token: string }>(
      "/reset-password-and-sign-in",
      {
        method: "POST",
        body: input,
      },
    );

    const failure = error as AuthFetchError | null;
    if (failure || !data?.token) {
      throw new CoreAPIError(
        failure?.message ?? "Failed to save the password",
        failure?.status,
        failure?.code,
      );
    }

    this.storeJwtToken(data.token);
    forgetResetToken();
    clearResetSaveUnclear();
  };

  /**
   * A first password for the signed-in user. The server never replaces one
   * this way (`PASSWORD_ALREADY_SET`): that takes the emailed reset link.
   */
  setPassword = async (input: { newPassword: string }): Promise<void> => {
    const { error } = await this.client.users.me.password.post(input);

    if (error) {
      // Typed as the 400/401/422 bodies, but a proxy error can send anything.
      const body = error.value as { code?: string; message?: string } | null;
      throw new CoreAPIError(
        body?.message ?? "Failed to save the password",
        error.status,
        body?.code,
      );
    }
  };

  /**
   * Swap the checkout's one-time code for a session token. Returns false on
   * any failure (used, expired, unpaid, offline): the caller carries on with
   * whatever login is already saved.
   */
  exchangeHandoffCode = async (code: string): Promise<boolean> => {
    try {
      const { data, error } = await this.authClient.firstTime.exchange({
        code,
      });
      if (error || !data?.token) {
        console.warn("coreapi: handoff code refused", error?.status);
        return false;
      }
      this.storeJwtToken(data.token);
      return true;
    } catch (err) {
      console.warn("coreapi: handoff code exchange failed", err);
      return false;
    }
  };

  /**
   * A one-time ticket for Lovelica's live-chat socket (`?ticket=`): a browser
   * can't put the bearer token on a WebSocket. Single use, 1 minute, so get a
   * new one for every connect. Never throws.
   */
  getChatTicket = async (): Promise<ChatTicketResult> => {
    try {
      const { data, error } = await withTimeout((signal) =>
        this.authClient.oneTimeToken.generate({ fetchOptions: { signal } }),
      );
      if (error) {
        return {
          ok: false,
          reason: meansNoSession(error.status) ? "no-session" : "failed",
        };
      }
      if (!data?.token) return { ok: false, reason: "failed" };
      return { ok: true, ticket: data.token };
    } catch (err) {
      console.warn("coreapi: chat ticket request failed", err);
      return { ok: false, reason: "failed" };
    }
  };

  storeJwtToken = (token: string): void => {
    localStorage.setItem(BEARER_TOKEN_KEY, token);
  };

  logout = async (): Promise<void> => {
    try {
      const { error } = await this.authClient.signOut();

      if (error) {
        throw new Error(error.message);
      }
    } finally {
      // Drop the local token even if the server call failed — otherwise a
      // still-valid bearer token resurrects the session on the next load.
      localStorage.removeItem(BEARER_TOKEN_KEY);
    }
  };

  /** The logged-in user. Never by id: bible-core keeps the id lookups for services. */
  getMe = async <
    P extends BaseUserCustomProperties = BaseUserCustomProperties,
  >(): Promise<User<P>> => {
    const { data, error } = await this.client.users.me.get();

    if (error) {
      throw new CoreAPIError(
        error.value?.message ?? "Failed to fetch the current user",
        error.status,
      );
    }

    if (!data) {
      throw new Error("Failed to fetch the current user: data is missing");
    }

    return resolveUser<P>(data);
  };

  /**
   * Is the stored token still good for a session? `false` when the server
   * says there is none (expired, revoked, signed out elsewhere); throws
   * when it couldn't be asked (offline, 5xx, a proxy's 403, no answer in
   * 10 s), so callers don't mistake a network blip for a logout.
   */
  hasAuthSession = async (): Promise<boolean> => {
    const { data, error } = await withTimeout((signal) =>
      this.authClient.getSession({ fetchOptions: { signal } }),
    );

    if (error) {
      if (meansNoSession(error.status)) return false;
      throw new Error(error.message ?? "Failed to check auth session");
    }

    return Boolean(data);
  };

  /**
   * The current session, or `null` when there is none (no token, expired,
   * signed out elsewhere — the server answers 200 with `null`). Throws when
   * the server couldn't be asked (offline) or answered with an error, so
   * "not signed in" and "couldn't check" never look the same.
   */
  getAuthSession = async (): Promise<AuthSession | null> => {
    const { data, error } = await this.authClient.getSession();

    if (error) {
      if (meansNoSession(error.status)) return null;
      throw new CoreAPIError(
        error.message ?? "Failed to fetch auth session",
        error.status,
      );
    }

    if (!data) return null;

    return {
      userEmail: data.user.email,
      userID: data.user.id,
    };
  };

  updateUser = async <
    P extends BaseUserCustomProperties = BaseUserCustomProperties,
  >(
    input: UserProfileUpdate & {
      id: string;
      custom_properties?: Partial<P>;
    },
  ): Promise<UpdatedUser<P>> => {
    const { data, error } = await this.client.updateUserProfile.post(input);

    if (error) {
      throw new Error(error.value.message);
    }

    if (!data) {
      throw new Error("Failed to update user: data is missing");
    }

    return resolveUpdatedUser<P>(data);
  };

  /**
   * Report something the signed-in user did to the core API's audit trail —
   * the account history used for support and dispute defence, **not** product
   * analytics (that is `services/tracking`). Use it for the few things only
   * the browser can witness: that the authenticated app really rendered, that
   * audio really played.
   *
   * `action` is `<domain>.<event>` in lowercase snake case
   * (`app.authenticated_view`). Any name works except the ones the server
   * records itself (logout, subscription cancel, profile edit), which it
   * refuses from a browser. The server — not this call — decides the user,
   * the time, the IP and the source (`webapp:<project>`).
   *
   * Fire-and-forget: returns at once, never throws, and does nothing while
   * logged out. `keepalive` lets an event sent during navigation or tab close
   * still reach the server.
   */
  audit = (action: string, options: AuditEventOptions = {}): void => {
    try {
      // Nothing to report while logged out — the server would answer 401.
      if (!getAuthorizationHeaders().Authorization) return;

      void this.client.audit.events
        .post(
          { events: [{ action, ...options }] },
          // Lets an event sent during navigation or tab close still arrive.
          { fetch: { keepalive: true } },
        )
        .catch(() => {
          // Offline or blocked: an audit event is never worth surfacing.
        });
    } catch {
      // localStorage unavailable, coreapi not configured yet — same as above.
    }
  };

  /**
   * Cancels every forward-billing subscription the user holds for this
   * project (`x-nt-project`), whichever payment provider each one is on.
   * Idempotent: an already-canceled subscription answers SUCCESS.
   */
  cancelSubscription = async (input?: {
    cancelReason?: string;
  }): Promise<void> => {
    const { error } = await this.client.subscriptions.cancel.post(input);

    if (error) {
      throw new Error(error.value.message);
    }
  };

  grantAgeConsent = async (input: {
    userId: string;
    consentVersion: string;
  }): Promise<void> => {
    const { error } = await this.client.consent.age.grant.post(input);

    if (error) {
      throw new Error(error.value.message);
    }
  };

  /**
   * Push the authed user's next bill out by `days` (1-30) — "extend trial".
   * Server allows this once per subscription and only during the first billing
   * cycle; check `trialExtendedAt` on the subscription summary before offering it.
   */
  postponeNextBill = async (days: number) => {
    const { error } = await this.client.subscriptions.postponeNextBill.post({
      days,
    });

    if (error) {
      throw new Error(error.value.message);
    }
  };
}

export const coreapi = new CoreAPI();
